import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import {
  SaveFundAccountAccessApiResponse,
  SaveFundAccountAccessRequest,
  SaveFundAccountAccessResponse,
} from "@myProfile/models";
import { openNotificationThunk } from "@store/Notification/thunks";

const SAVE_FUND_ACCOUNT_ACCESS_URL = "/contacts/v1/user/saveFundAccountAccess";

const SAVE_ERROR_MESSAGE = "We couldn't save your changes. Please try again.";

// The backend serializes this response PascalCase (no [JsonProperty]/camelCase resolver), so read
// both casings and collapse to the canonical camelCase shape the rest of the app expects.
const normalizeResponse = (data: SaveFundAccountAccessApiResponse): SaveFundAccountAccessResponse => ({
  success: data.success ?? data.Success ?? false,
  message: data.message ?? data.Message ?? "",
  guid: data.guid ?? data.Guid ?? "",
  removedCount: data.removedCount ?? data.RemovedCount ?? 0,
  editedCount: data.editedCount ?? data.EditedCount ?? 0,
  requestedCount: data.requestedCount ?? data.RequestedCount ?? 0,
});

export const saveFundAccountAccessThunk = createAsyncThunk<
  SaveFundAccountAccessResponse,
  SaveFundAccountAccessRequest,
  ThunkApiConfig
>("myProfile/saveFundAccountAccess", async (request, { extra, dispatch, rejectWithValue }) => {
  try {
    const response = await extra<SaveFundAccountAccessApiResponse, SaveFundAccountAccessRequest>(
      SAVE_FUND_ACCOUNT_ACCESS_URL,
      {
        method: "POST",
        body: request,
      }
    );

    if (!response || !response.data) {
      dispatch(openNotificationThunk({ variant: "warning", text: SAVE_ERROR_MESSAGE }));

      return rejectWithValue(SAVE_ERROR_MESSAGE);
    }

    const result = normalizeResponse(response.data);

    if (!result.success) {
      const message = result.message || SAVE_ERROR_MESSAGE;

      dispatch(openNotificationThunk({ variant: "warning", text: message }));

      return rejectWithValue(message);
    }

    dispatch(
      openNotificationThunk({
        variant: "info",
        text: result.message || "Your changes have been saved.",
      })
    );

    return result;
  } catch (error: unknown) {
    let errorMessage = SAVE_ERROR_MESSAGE;

    if (typeof error === "object" && error !== null && "response" in error) {
      const axiosError = error as { response?: { data?: { message?: string } } };

      errorMessage = axiosError.response?.data?.message || errorMessage;
    }

    dispatch(openNotificationThunk({ variant: "warning", text: errorMessage }));

    return rejectWithValue(errorMessage);
  }
});
